const POINTER_EVENT_TYPES = ["pointermove", "pointerdown", "pointerup", "pointerleave"];
const MOUSE_EVENT_TYPES = ["mousemove", "mousedown", "click", "mouseleave"];
const INTERACTION_LISTENER_OPTIONS = {capture: true, passive: true};
const UNBOUNDED_EVENT_TYPES = new Set(["pointerleave", "mouseleave"]);
const EDITOR_INTERACTION_SELECTOR = ".ufo-fab--container";

export default {
    data() {
        return {
            animatedBackgroundInteractionRootEl: null,
            animatedBackgroundInteractionListeners: [],
            animatedBackgroundMouseMoveRaf: null,
            animatedBackgroundPendingMouseMoveEvent: null,
            animatedBackgroundBridgeSetupRaf: null,
            isForwardingAnimatedBackgroundEvent: false
        };
    },
    mounted() {
        this.syncAnimatedBackgroundInteractionBridge();
    },
    beforeUnmount() {
        this.stopAnimatedBackgroundInteractionBridge();
    },
    watch: {
        showAnimatedBackground() {
            this.syncAnimatedBackgroundInteractionBridge();
        }
    },
    methods: {
        syncAnimatedBackgroundInteractionBridge() {
            if (this.showAnimatedBackground) {
                this.startAnimatedBackgroundInteractionBridge();
                return;
            }
            this.stopAnimatedBackgroundInteractionBridge();
        },
        startAnimatedBackgroundInteractionBridge() {
            this.stopAnimatedBackgroundInteractionBridge();
            const sectionRoot = this.$el?.closest?.(".ufo-sec");
            if (!sectionRoot) return;

            this.animatedBackgroundInteractionRootEl = sectionRoot;
            this.addAnimatedBackgroundInteractionListeners(sectionRoot);

            this.animatedBackgroundBridgeSetupRaf = requestAnimationFrame(() => {
                this.animatedBackgroundBridgeSetupRaf = null;
                this.getAnimatedBackgroundOverlayInteractionRoots().forEach(root => {
                    this.addAnimatedBackgroundInteractionListeners(root);
                });
            });
        },
        stopAnimatedBackgroundInteractionBridge() {
            if (this.animatedBackgroundBridgeSetupRaf) {
                cancelAnimationFrame(this.animatedBackgroundBridgeSetupRaf);
                this.animatedBackgroundBridgeSetupRaf = null;
            }
            this.animatedBackgroundInteractionListeners.forEach(listener => {
                listener.element.removeEventListener(listener.type, listener.handler, listener.options);
            });
            this.animatedBackgroundInteractionListeners = [];
            this.animatedBackgroundInteractionRootEl = null;
            if (this.animatedBackgroundMouseMoveRaf) {
                cancelAnimationFrame(this.animatedBackgroundMouseMoveRaf);
                this.animatedBackgroundMouseMoveRaf = null;
            }
            this.animatedBackgroundPendingMouseMoveEvent = null;
        },
        addAnimatedBackgroundInteractionListener(element, type, handler, options) {
            element.addEventListener(type, handler, options);
            this.animatedBackgroundInteractionListeners.push({element, type, handler, options});
        },
        addAnimatedBackgroundInteractionListeners(element) {
            POINTER_EVENT_TYPES.forEach(type => {
                this.addAnimatedBackgroundInteractionListener(element, type, this.handleAnimatedBackgroundPointerEvent, INTERACTION_LISTENER_OPTIONS);
            });
            MOUSE_EVENT_TYPES.forEach(type => {
                this.addAnimatedBackgroundInteractionListener(element, type, this.handleAnimatedBackgroundMouseEvent, INTERACTION_LISTENER_OPTIONS);
            });
        },
        getAnimatedBackgroundOverlayInteractionRoots() {
            const sectionRoot = this.animatedBackgroundInteractionRootEl;
            if (!sectionRoot) return [];

            return Array.from(document.querySelectorAll(".ufo-sec--navigation.nav-overlay"))
                .filter(root => root !== sectionRoot);
        },
        shouldForwardAnimatedBackgroundSourceEvent(event) {
            if (UNBOUNDED_EVENT_TYPES.has(event.type)) return true;
            if (event.currentTarget === this.animatedBackgroundInteractionRootEl) return true;
            const root = this.animatedBackgroundInteractionRootEl;
            if (!root || !Number.isFinite(event.clientX) || !Number.isFinite(event.clientY)) return false;

            const rect = root.getBoundingClientRect();
            return event.clientX >= rect.left
                && event.clientX <= rect.right
                && event.clientY >= rect.top
                && event.clientY <= rect.bottom;
        },
        shouldHandleAnimatedBackgroundSourceEvent(event) {
            if (this.isForwardingAnimatedBackgroundEvent) return;
            if (this.isAnimatedBackgroundEditorInteractionEvent(event)) return;
            return this.shouldForwardAnimatedBackgroundSourceEvent(event);
        },
        isAnimatedBackgroundEditorInteractionEvent(event) {
            return !!event.target?.closest?.(EDITOR_INTERACTION_SELECTOR);
        },
        handleAnimatedBackgroundPointerEvent(event) {
            if (!this.shouldHandleAnimatedBackgroundSourceEvent(event)) return;
            this.forwardPointerEventToAnimatedBackground(event, event.type);
        },
        getAnimatedBackgroundInteractionTarget() {
            const host = this.$refs.animatedBackground?.$el;
            if (!host) return null;
            return host.querySelector("canvas") || host;
        },
        handleAnimatedBackgroundMouseEvent(event) {
            if (!this.shouldHandleAnimatedBackgroundSourceEvent(event)) return;
            if (event.type === "mousemove") {
                this.throttleAnimatedBackgroundMouseMove(event);
                return;
            }
            this.forwardMouseEventToAnimatedBackground(event, event.type);
        },
        throttleAnimatedBackgroundMouseMove(event) {
            this.animatedBackgroundPendingMouseMoveEvent = event;
            if (this.animatedBackgroundMouseMoveRaf) return;
            this.animatedBackgroundMouseMoveRaf = requestAnimationFrame(() => {
                this.animatedBackgroundMouseMoveRaf = null;
                const sourceEvent = this.animatedBackgroundPendingMouseMoveEvent;
                this.animatedBackgroundPendingMouseMoveEvent = null;
                if (sourceEvent) {
                    this.forwardMouseEventToAnimatedBackground(sourceEvent, "mousemove");
                }
            });
        },
        forwardMouseEventToAnimatedBackground(sourceEvent, type) {
            if (!this.showAnimatedBackground) return;
            const target = this.getAnimatedBackgroundInteractionTarget();
            if (!target) return;

            const forwardedEvent = new MouseEvent(type, {
                bubbles: false,
                cancelable: true,
                composed: true,
                clientX: sourceEvent.clientX,
                clientY: sourceEvent.clientY,
                screenX: sourceEvent.screenX,
                screenY: sourceEvent.screenY,
                button: sourceEvent.button,
                buttons: sourceEvent.buttons,
                ctrlKey: sourceEvent.ctrlKey,
                shiftKey: sourceEvent.shiftKey,
                altKey: sourceEvent.altKey,
                metaKey: sourceEvent.metaKey
            });
            this.isForwardingAnimatedBackgroundEvent = true;
            try {
                target.dispatchEvent(forwardedEvent);
            } finally {
                this.isForwardingAnimatedBackgroundEvent = false;
            }
        },
        forwardPointerEventToAnimatedBackground(sourceEvent, type) {
            if (!this.showAnimatedBackground) return;
            const target = this.getAnimatedBackgroundInteractionTarget();
            if (!target) return;

            let forwardedEvent;
            if (typeof PointerEvent !== "undefined") {
                forwardedEvent = new PointerEvent(type, {
                    bubbles: false,
                    cancelable: true,
                    composed: true,
                    clientX: sourceEvent.clientX,
                    clientY: sourceEvent.clientY,
                    screenX: sourceEvent.screenX,
                    screenY: sourceEvent.screenY,
                    button: sourceEvent.button,
                    buttons: sourceEvent.buttons,
                    ctrlKey: sourceEvent.ctrlKey,
                    shiftKey: sourceEvent.shiftKey,
                    altKey: sourceEvent.altKey,
                    metaKey: sourceEvent.metaKey,
                    pointerId: sourceEvent.pointerId,
                    pointerType: sourceEvent.pointerType,
                    isPrimary: sourceEvent.isPrimary,
                    pressure: sourceEvent.pressure,
                    width: sourceEvent.width,
                    height: sourceEvent.height
                });
            } else {
                forwardedEvent = new MouseEvent(type, {
                    bubbles: false,
                    cancelable: true,
                    composed: true,
                    clientX: sourceEvent.clientX,
                    clientY: sourceEvent.clientY,
                    screenX: sourceEvent.screenX,
                    screenY: sourceEvent.screenY,
                    button: sourceEvent.button,
                    buttons: sourceEvent.buttons,
                    ctrlKey: sourceEvent.ctrlKey,
                    shiftKey: sourceEvent.shiftKey,
                    altKey: sourceEvent.altKey,
                    metaKey: sourceEvent.metaKey
                });
            }

            this.isForwardingAnimatedBackgroundEvent = true;
            try {
                target.dispatchEvent(forwardedEvent);
            } finally {
                this.isForwardingAnimatedBackgroundEvent = false;
            }
        }
    }
};
